// @flow

import React from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { Button } from '@riseart/common';
import { ArtCard } from '@riseart/art';
import { dataToListProps } from 'shared_services/riseart/utils/artDataUtils';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { selectUnitSystem } from 'shared_services/redux/selectors/unitSystem';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';

type ContentArtProps = {
  art: Object,
  artDirectionSize: string,
  disableHeadingTags?: boolean,
  lazyload?: Object,
};

const LAZYLOAD_OFFSET_PX = 200;

/**
 * ArtistArtGridArt
 *
 * @param {ContentArtProps} props
 * @returns {Node}
 */
export function ArtistArtGridArt({
  art,
  disableHeadingTags,
  artDirectionSize,
  lazyload = { once: true, offset: LAZYLOAD_OFFSET_PX },
}: ContentArtProps): Node {
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const { unitSystem } = useSelector(selectUnitSystem, shallowEqual);

  // Do not render component if art is not provided
  if (!art) {
    return null;
  }

  const artImage = extractImageFileFromData.artImage(art.images, 'TYPE_FLAT_MASTER');
  const {
    title,
    price,
    artworkUrl,
    artistName,
    artistUrl,
    additionalContent,
    pictureProperties,
    image = {},
  } = dataToListProps(
    { ...art, ...(artImage ? { artImage: { ...artImage, ...artImage.file } } : null) },
    locale,
    artDirectionSize,
    formatMessage,
    undefined,
    unitSystem,
  );

  return (
    <ArtCard
      displayStyle={8}
      artistName={artistName}
      artistUrl={artistUrl}
      linkProps={{ title }}
      showImagePlaceholder
      pictureProps={{
        ...pictureProperties,
        alt: title,
        lazyload: lazyload || null,
        image: {
          width: image.width,
          height: image.height,
        },
      }}
      artworkUrl={artworkUrl}
      artworkTitle={title}
      price={price}
      additionalContent={additionalContent}
      disableHeadingTags={disableHeadingTags}
      moreInfo={
        <Button type="secondary" size="small" isAnchor to={artworkUrl}>
          {formatMessage({ id: 'common.moreInfo' })}
        </Button>
      }
    />
  );
}
